import { Injectable, inject } from '@angular/core';
import { MobileResolutionService } from './mobile-resolution.service';
import { CookieService } from 'ngx-cookie-service';
import { SubscriptionType } from '../account/account.service';

@Injectable({
  providedIn: 'root'
})
export class WebviewService {

  public readonly mobileResolutionService = inject(MobileResolutionService);
  private readonly cookieService = inject(CookieService);

  private batteryOptimizationSettingsRequired: boolean = false;

  async resolve() {
    this.InitGPSTWebviewFunctions();
  }

  public IsGPSTWebview(): boolean {
    return window.navigator.userAgent.includes("GPSTWebView");
  }

  private InitGPSTWebviewFunctions(): void {
    // The native apps inject their real bridge implementations (startSubscription,
    // startMicrophoneInteraction, playAudio, ...) onto webViewObj, and this resolver can run
    // after that injection. The object must therefore never be rebuilt from scratch: that
    // would replace the injected functions with the no-op stubs below, and every
    // web->native call would silently do nothing. Keep whatever exists and only fill gaps.
    if (!window['webViewObj']) {
      window['webViewObj'] = function Class() { };
    }

    // Web-implemented entries called by native — ours is the authoritative implementation.
    window['webViewObj']['goBack'] = () => {
      const p = this.mobileResolutionService.getMobileVisiblePanel();
      if (p == "slider" || p == "chargingstation" || p == "chargingstation" || p == "wind") {
        this.mobileResolutionService.setMobileVisiblePanel("");
        return true;
      }
      else if (document.getElementsByClassName("mat-dialog-container").length > 0) {
        if (document.getElementsByClassName("cdk-overlay-backdrop-showing").length > 0) {
          (document.getElementsByClassName('cdk-overlay-backdrop-showing')[document.getElementsByClassName("cdk-overlay-backdrop-showing").length - 1] as HTMLElement).click();
        }
        return true;
      }
      return false;
    };

    window['webViewObj']['getAccessToken'] = () => {
      return this.getAccessToken();
    };

    // Everything below is stubbed only when nothing provides it yet — either native code
    // injected earlier, or a web callback a service registered before this resolver ran.
    const stubIfMissing = (fnName: string, stub: () => any = function name() { }): void => {
      if (!window['webViewObj'][fnName]) {
        window['webViewObj'][fnName] = stub;
      }
    };

    [
      'setStartCoordsToYourGeolocation', 'startNavigation', 'stopNavigation',

      'rhaPolygonUpdate', 'routeUpdate',

      'openUrl', 'downloadFile', 'playAudio',

      'facebookSignIn', 'facebookLogOut', 'facebookSignInCallBack',

      'googleSignIn', 'googleLogOut', 'googleSignInCallBack',

      'appleSignIn', 'appleLogOut', 'appleSignInCallBack',

      'setAccessToken', 'clearAccessToken',

      'setUserId', 'setUserLanguage',

      'startSubscription', 'subscriptionSuccessCallback', 'openManageSubscription',
      'onManageSheetClosed', 'subscriptionCancelCallback',

      'smartConnectionSignIn', 'smartConnectionSignInCallback',

      'openBatteryOptimizationWindow',

      'OnSpeachRecognitionError', 'startMicrophoneInteraction', 'microphoneInteractionCallback'
    ].forEach((fnName) => stubIfMissing(fnName));

    stubIfMissing('getBatteryOptimizationActive', function name() {
      return false;
    });
  }

  /**
   * Parameter order is the one native actually calls with (timestamp first, speed last) — every
   * argument is a number, so a wrong order here type-checks happily and only shows up as garbled
   * positions at runtime.
   */
  public InitLocationFunction(locationFunction: (timestamp: number, latitude: number, longitude: number, altitude: number, accuracy: number, altitudeAccuracy: number, heading: number, speed: number) => void): void {
    window['webViewObj']['setWebViewLocation'] = locationFunction;
  }

  public InitGoToCurrentLocationFunction(goToCurrLocFunction: () => void): void {
    window['webViewObj']['goToCurrentLocation'] = goToCurrLocFunction;
  }

  public AddFacebookSignInCallback(callback: (isSucces: boolean, userId: string, accessToken: string) => void): void {
    window['webViewObj']['facebookSignInCallBack'] = callback;
  }

  public AddGoogleSignInCallback(callback: (isSucces: boolean, userId: string, accessToken: string) => void): void {
    window['webViewObj']['googleSignInCallBack'] = callback;
  }

  public AddAppleSignInCallback(callback: (isSucces: boolean, userId: string, accessToken: string) => void): void {
    window['webViewObj']['appleSignInCallBack'] = callback;
  }

  public AddSmartConnectionSignInCallback(callback: (isSucces: boolean, vehicleId: number) => void): void {
    window['webViewObj']['smartConnectionSignInCallback'] = callback;
  }

  public OpenUrl(url: string): void {
    window['webViewObj'].openUrl(url);
  }

  public DownloadFile(content: string, fileName: string): void {
    window['webViewObj'].downloadFile(content, fileName);
  }

  public RhaPolygonUpdate(rhaPolygon: string): void {
    window['webViewObj'].rhaPolygonUpdate(rhaPolygon);
  }

  public RouteUpdate(route: string): void {
    window['webViewObj'].routeUpdate(route);
  }

  public InitSetBattery(batteryFunction: (battery: number) => void): void {
    window['webViewObj']['setBattery'] = batteryFunction;
  }

  // ===== Android Auto planning handoff =====
  //
  // Outbound only. What the car sends back does not arrive through this bridge at all: it is
  // written into localStorage before this app boots, so there is nothing here to receive it.
  //
  // Talks to the native object directly where it exists, falling back to the webViewObj wrapper.
  // Deliberately absent from InitGPSTWebviewFunctions: that method rebuilds webViewObj from
  // scratch, and stubbing this would clobber the native wrapper whenever the resolver happens to
  // run last.

  private nativeBridge(): any {
    return (window as any)['nativeAndroid'];
  }

  /** Hand the web's current mode and waypoints to the native side. */
  public PushWebPlanningState(snapshotJson: string): void {
    const native = this.nativeBridge();
    if (native?.pushWebPlanningState) {
      native.pushWebPlanningState(snapshotJson);
      return;
    }
    window['webViewObj']?.['pushWebPlanningState']?.(snapshotJson);
  }

  public StartNavigation(): void {
    window['webViewObj'].startNavigation();
  }

  public StopNavigation(): void {
    window['webViewObj'].stopNavigation();
  }

  public SetStartCoordsToYourGeolocation(): void {
    window['webViewObj'].setStartCoordsToYourGeolocation();
  }

  public PlayAudio(array: any[]): void {
    window['webViewObj'].playAudio(new Blob(array, { type: "arraybuffer" }));
  }

  public PlayAudioAndroid(audioFile: any): void {
    window['webViewObj'].playAudio(audioFile);
  }

  public FacebookSignIn(): void {
    window['webViewObj'].facebookSignIn();
  }

  public FacebookLogout(): void {
    window['webViewObj'].facebookLogOut();
  }

  public GoogleSignIn(): void {
    window['webViewObj'].googleSignIn();
  }

  public GoogleLogOut(): void {
    window['webViewObj'].googleLogOut();
  }

  public AppleSignIn(): void {
    window['webViewObj'].appleSignIn();
  }

  public AppleLogOut(): void {
    window['webViewObj'].appleLogOut();
  }

  public getAccessToken(): string {
    let evtoken = this.cookieService.get("evt");
    if (evtoken && evtoken != "null") {
      if (evtoken.slice(-6) == "social") {
        return evtoken.slice(0, -6);
      }
      else {
        return evtoken;
      }
    }
    else{
      return "";
    }
  }

  public SetAccessToken(accessToken:string, expires: number): void{
    window['webViewObj'].setAccessToken(accessToken, expires);
  }

  public ClearAccessToken(): void{
    window['webViewObj'].clearAccessToken();
  }

  public StartSubscription(subscriptionType:SubscriptionType): void{
    window['webViewObj'].startSubscription(subscriptionType);
  }

  public addSubscriptionSuccessCallback(callback: (isSuccess: boolean, subscriptionType: SubscriptionType) => void): void {
    window['webViewObj']['subscriptionSuccessCallback'] = callback;
  }

  public addSubscriptionCancelCallback(callback: () => void): void {
    window['webViewObj']['subscriptionCancelCallback'] = callback;
  }

  public OpenManageSubscription(): void{
    window['webViewObj'].openManageSubscription();
  }

  public addOnManageSheetClosed(callback: (isSuccess: boolean) => void): void {
    window['webViewObj']['onManageSheetClosed'] = callback;
  }

  public SetUserId(userId:string): void{
    window['webViewObj'].setUserId(userId);
  }

  public SetUserLanguage(languageCode:string): void{
    window['webViewObj'].setUserLanguage(languageCode);
  }

  public SmartConnectionSignIn(UserCarId: number): void {
    window['webViewObj'].smartConnectionSignIn(UserCarId);
  }

  public OpenBatteryOptimizationWindow(): void {
    window['webViewObj'].openBatteryOptimizationWindow();
  }

  public setBatteryOptimizationSettingsRequired(batteryOptimizationSettingsRequired: boolean): void {
    this.batteryOptimizationSettingsRequired = batteryOptimizationSettingsRequired;
  }

  public getBatteryOptimizationSettingsRequired(): boolean {
    return this.batteryOptimizationSettingsRequired;
  }

  public getBatteryOptimizationActive(): boolean {
    return window['webViewObj'].getBatteryOptimizationActive();
  }

  public startMicrophoneInteraction(): void{
    window["webViewObj"].startMicrophoneInteraction();
  }

  public addMicrophoneInteractionCallback(callback: (permission: boolean) => void): void {
    window["webViewObj"]['microphoneInteractionCallback'] = callback;
  }

  public onSpeachRecognitionError(): void{
    window["webViewObj"].onSpeachRecognitionError();
  }

  public addOnSpeachRecognitionErrorCallback(callback: () => void): void {
    window['webViewObj']['OnSpeachRecognitionErrorCallback'] = callback;
  }
}
